import { useState } from 'react';
import { Plane, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { INPUT_CLS } from '@/components/brand/BrandKit';
import { Field, DateTimeRow, ContactFields, ReturnToggle, VehiclePicker, EstimateCard, ManagedConfirmation, ChoiceTabs, PickerBlock, DetailsHeading } from '@/components/booking/ManagedParts';
import { AirportRouteFields, CityRouteFields } from '@/components/booking/TransferRouteFields';
import { useManagedBooking } from '@/hooks/useManagedBooking';
import { bt } from '@/i18n/bookingI18n';
import { AIRPORTS, TRANSFER_VEHICLES, CITY_VEHICLES, money } from '@/data/transportPricing';
import { computeTransferEstimate, transferPriceFor, validateTransfer } from '@/components/booking/transferLogic';

const EMPTY_FORM = { name: '', email: '', phone: '', notes: '' };
const EMPTY_TRIP = { airportId: 'DXB', zoneId: '', fromIdx: '', toIdx: '', vehicleId: '', date: null, time: '', flight: '', returnTrip: false };
const idx = (s) => (s === '' ? null : Number(s));

function useTransferState() {
  const [mode, setMode] = useState('airport');
  const [trip, setTrip] = useState(EMPTY_TRIP);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const clearError = (k) => setErrors((p) => ({ ...p, [k]: undefined }));
  const patch = (changes, errorKey) => { setTrip((p) => ({ ...p, ...changes })); if (errorKey) clearError(errorKey); };
  const setField = (k, v) => { setForm((p) => ({ ...p, [k]: v })); clearError(k); };
  const switchMode = (m) => { setMode(m); patch({ vehicleId: '' }); setErrors({}); };
  const reset = () => { setTrip(EMPTY_TRIP); setForm(EMPTY_FORM); setErrors({}); };
  return { mode, trip, form, errors, setErrors, patch, setField, switchMode, reset };
}

export function TransferBooking({ lang = 'en' }) {
  const MODES = [{ id: 'airport', label: bt(lang, 'modeAirport'), icon: Plane }, { id: 'city', label: bt(lang, 'modeCity'), icon: MapPin }];
  const s = useTransferState();
  const b = useManagedBooking(lang);
  const { mode, trip, form, errors } = s;

  const airport = AIRPORTS.find((a) => a.id === trip.airportId);
  const zone = airport?.zones.find((z) => z.id === trip.zoneId);
  const vehicles = mode === 'airport' ? TRANSFER_VEHICLES : CITY_VEHICLES;
  const vehicle = vehicles.find((v) => v.id === trip.vehicleId);
  const fi = idx(trip.fromIdx);
  const ti = idx(trip.toIdx);
  const estimate = computeTransferEstimate({ mode, airport, zone, vehicle, fi, ti, returnTrip: trip.returnTrip, lang });

  const onSubmit = async () => {
    const e = validateTransfer({ mode, zoneId: trip.zoneId, fi, ti, vehicleId: trip.vehicleId, date: trip.date, time: trip.time, form, lang });
    s.setErrors(e);
    if (Object.keys(e).length || !estimate) return;
    const ok = await b.submit({
      service_type: 'transfer', date: trip.date.toISOString(), time: trip.time,
      duration: mode === 'airport' ? 'Airport Transfer' : 'Point-to-Point',
      pickup_type: mode === 'airport' ? 'airport' : 'location', pickup_location: estimate.pickup,
      dropoff_type: 'location', dropoff_location: estimate.dropoff, passengers: '',
      vehicle: `${vehicle.name} (${vehicle.models})`, flight_number: trip.flight, return_trip: trip.returnTrip,
      route: estimate.summary, price: estimate.total, ...form,
    });
    if (ok) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (b.submitted) {
    return (
      <ManagedConfirmation name={form.name} bookingRef={b.bookingRef} lang={lang} onReset={() => { b.reset(); s.reset(); }}
        rows={[{ label: bt(lang, 'routeWord'), value: estimate?.summary || '-' }, { label: bt(lang, 'estimatedTotal'), value: estimate ? money(estimate.total) : '-' }]} />
    );
  }

  return (
    <div data-testid="transfer-booking" className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
      <div className="lg:col-span-7 space-y-6">
        <ChoiceTabs options={MODES} value={mode} onChange={s.switchMode} testidPrefix="transfer-mode" />
        {mode === 'airport'
          ? <AirportRouteFields airport={airport} airportId={trip.airportId} onAirport={(v) => s.patch({ airportId: v, zoneId: '' })} zoneId={trip.zoneId} onZone={(v) => s.patch({ zoneId: v }, 'zone')} errors={errors} lang={lang} />
          : <CityRouteFields fromIdx={trip.fromIdx} onFrom={(v) => s.patch({ fromIdx: v }, 'from')} toIdx={trip.toIdx} onTo={(v) => s.patch({ toIdx: v }, 'to')} errors={errors} lang={lang} />}
        <PickerBlock label={bt(lang, 'vehicleClass')} error={errors.vehicle}>
          <VehiclePicker vehicles={vehicles} value={trip.vehicleId} onChange={(id) => s.patch({ vehicleId: id }, 'vehicle')} priceFor={(v) => transferPriceFor({ mode, zone, fi, ti, v })} lang={lang} />
        </PickerBlock>
        <DateTimeRow date={trip.date} setDate={(d) => s.patch({ date: d }, 'date')} time={trip.time} setTime={(t) => s.patch({ time: t }, 'time')} errors={errors} lang={lang} />
        {mode === 'airport' && (
          <Field label={bt(lang, 'flightOptional')}><Input data-testid="transfer-flight" value={trip.flight} onChange={(e) => s.patch({ flight: e.target.value })} placeholder={bt(lang, 'flightPh')} className={INPUT_CLS} /></Field>
        )}
        <ReturnToggle value={trip.returnTrip} onChange={(v) => s.patch({ returnTrip: v })} lang={lang} />
        <DetailsHeading lang={lang} />
        <ContactFields form={form} set={s.setField} errors={errors} lang={lang} />
      </div>
      <div className="lg:col-span-5">
        <EstimateCard lines={estimate ? estimate.lines : []} total={estimate ? estimate.total : 0} error={b.error} submitting={b.submitting} onSubmit={onSubmit} disabled={!estimate} lang={lang} note={bt(lang, 'transferNote')} />
      </div>
    </div>
  );
}
